// Racha diaria — el gancho de hábito de MiPAE (estilo Duolingo/Calm): el conteo
// de "días seguidos" que no quieres romper. Es DISTINTA de la racha de turnos y
// de reflexión (esas son por actividad y toleran el calendario irregular). Esta
// es EL hábito de la app: cada día que el usuario hace algo con sentido (un reto,
// una reflexión, documentar su turno, un repaso…), suma un día.
//
// Filosofía anti-culpa para un público clínico: cadencia DIARIA (la presión sana
// que engancha), pero con ESCUDOS que protegen días perdidos — un turno de noche
// brutal no te destruye la cadena. Se gana un escudo cada pocos días de racha.

import { supabase } from './supabase';
import { diasEntre, fechaKey } from './turnoRacha';
import { publicarWidget } from './widgetBridge';
import { calcularTurnoDelDia, addDays } from '../utils/calendario';

export const ESCUDOS_MAX = 2;          // tope de escudos acumulables
export const DIAS_POR_ESCUDO = 7;      // ganas 1 escudo cada 7 días de racha
export const HITOS = [7, 30, 60, 100, 180, 365]; // celebraciones sobrias

export const DIAS_LOCAL_MAX = 120;     // tope de días guardados en el mapa local

// El backend solo guarda la fecha del último día activo + el total acumulado
// (no CUÁLES días fueron activos), así que no alcanza para pintar un mapa del
// mes. Este registro es puramente local (localStorage, por usuario): no viaja
// a Supabase, no necesita migración, y si el usuario cambia de dispositivo
// simplemente empieza un mapa nuevo — no afecta la racha real, que sigue
// viviendo en la BD.
function claveDiasActivosLocal(userId) {
  return `mipae:racha:dias:${userId}`;
}

// Perdón por calendario (maqueta aprobada 31-jul, mecanismo Duolingo con
// sentido clínico): MiPAE SABE cuándo no te tocaba turno. Cuenta cuántos de
// los días estrictamente ENTRE `desde` y `hasta` ('YYYY-MM-DD') eran libres
// según la rotación — esos días no son "perdidos", son tu descanso. Sin
// rotación configurada no se perdona nada (calcularTurnoDelDia devolvería
// 'libre' para todo y la racha se volvería eterna). Cap de 90 días: más allá
// la cadena no sobrevive de ninguna forma (escudos max 2).
export const DIAS_PERDON_MAX = 90;
export function diasLibresEntre(desde, hasta, rotacion, overrides = null) {
  if (!desde || !hasta || !rotacion?.patron || !rotacion?.fecha_inicio) return 0;
  const gap = diasEntre(desde, hasta);
  if (gap == null || gap < 2 || gap > DIAS_PERDON_MAX) return 0;
  const ovMap = overrides instanceof Map
    ? overrides
    : new Map((Array.isArray(overrides) ? overrides : []).map((o) => [o.fecha, o]));
  const [y, m, d] = desde.split('-').map(Number);
  const base = new Date(y, m - 1, d);
  let libres = 0;
  for (let i = 1; i < gap; i++) {
    const dia = addDays(base, i);
    const key = fechaKey(dia);
    const turno = calcularTurnoDelDia(dia, rotacion, ovMap.get(key) || null);
    if (turno.tipo === 'libre') libres += 1;
  }
  return libres;
}

// Cálculo PURO de la racha al registrar actividad HOY.
//   prev = { racha, max, fecha ('YYYY-MM-DD'|null), escudos, dias }
//   diasLibres = de los días del hueco, cuántos eran libres por calendario
//   (los aporta el caller vía diasLibresEntre; 0 = comportamiento clásico).
// Devuelve el nuevo estado + banderas para la UI:
//   { racha, max, fecha, escudos, dias, subio, usoEscudo, ganoEscudo, hito,
//     reset, yaHoy, perdonCalendario }
export function siguienteRachaDiaria(prev = {}, hoy = fechaKey(), diasLibres = 0) {
  const racha0   = Number(prev.racha) || 0;
  const max0     = Number(prev.max) || 0;
  const escudos0 = Math.max(0, Math.min(ESCUDOS_MAX, Number(prev.escudos) || 0));
  const dias0    = Number(prev.dias) || 0;
  const fecha0   = prev.fecha || null;
  const gap      = diasEntre(fecha0, hoy); // null si fecha0 es null

  // Mismo día: idempotente — hacer varias actividades no dobla el conteo.
  if (gap === 0) {
    return { racha: racha0, max: max0, fecha: hoy, escudos: escudos0, dias: dias0,
             subio: false, usoEscudo: 0, ganoEscudo: 0, hito: null, reset: false, yaHoy: true,
             perdonCalendario: 0 };
  }

  let racha, escudos = escudos0, usoEscudo = 0, reset = false, perdonCalendario = 0;
  if (fecha0 === null || gap === null) {
    racha = 1;                                  // primer día activo
  } else if (gap === 1 || gap < 0) {
    racha = racha0 + 1;                          // consecutivo (o reloj adelantado: no castigar)
  } else {
    // gap >= 2: se perdieron (gap-1) días. Primero perdona el CALENDARIO
    // (los días libres de tu rotación no cuentan como perdidos: descansar
    // no rompe nada); lo que quede lo cubren los escudos.
    const libres = Math.max(0, Math.min(gap - 1, Number(diasLibres) || 0));
    const perdidos = (gap - 1) - libres;
    if (escudos0 >= perdidos) {
      escudos = escudos0 - perdidos;            // los escudos sostienen la cadena
      usoEscudo = perdidos;
      perdonCalendario = libres;
      racha = racha0 + 1;
    } else {
      racha = 1;                                 // se rompió: vuelve a empezar hoy
      reset = true;                              // (los escudos ganados se conservan)
    }
  }

  // Ganar escudos: cada DIAS_POR_ESCUDO de racha, +1 (hasta el tope). Se otorga
  // al cruzar el múltiplo, no en un reinicio.
  let ganoEscudo = 0;
  if (!reset && racha % DIAS_POR_ESCUDO === 0 && escudos < ESCUDOS_MAX) {
    escudos += 1; ganoEscudo = 1;
  }

  const dias = dias0 + 1;
  const max  = Math.max(max0, racha);
  const hito = HITOS.includes(racha) && racha > racha0 ? racha : null;

  return { racha, max, fecha: hoy, escudos, dias,
           subio: true, usoEscudo, ganoEscudo, hito, reset, yaHoy: false, perdonCalendario };
}

// ¿La racha sigue viva hoy? (la última actividad es hoy, ayer, o dentro de lo
// que el calendario + los escudos podrían cubrir). Para mostrarla sin que
// aparezca "muerta". `diasLibres` es opcional: sin él, comportamiento clásico.
export function rachaViva(fecha, escudos = 0, hoy = fechaKey(), diasLibres = 0) {
  if (!fecha) return false;
  const gap = diasEntre(fecha, hoy);
  if (gap == null) return false;
  if (gap <= 1) return true;
  const libres = Math.max(0, Math.min(gap - 1, Number(diasLibres) || 0));
  return (gap - 1 - libres) <= Math.max(0, escudos); // el calendario y los escudos aún podrían sostenerla
}

// Lee el estado de la racha diaria del perfil. Best-effort: ceros si falla.
export async function cargarRachaDiaria(userId) {
  const vacio = { racha: 0, max: 0, fecha: null, escudos: 0, dias: 0 };
  if (!userId) return vacio;
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('racha_diaria, racha_diaria_max, racha_diaria_fecha, racha_diaria_escudos, dias_activos_total')
      .eq('user_id', userId)
      .maybeSingle();
    if (error || !data) return vacio;
    const estado = {
      racha:   Number(data.racha_diaria) || 0,
      max:     Number(data.racha_diaria_max) || 0,
      fecha:   data.racha_diaria_fecha || null,
      escudos: Number(data.racha_diaria_escudos) || 0,
      dias:    Number(data.dias_activos_total) || 0,
    };
    // Cada carga (ej. abrir el Home) refresca de paso el widget nativo.
    publicarRachaWidget(estado);
    return estado;
  } catch {
    return vacio;
  }
}

// Lee los días activos guardados localmente (array 'YYYY-MM-DD' ascendente).
// Puro dato visual para el mini-mapa del mes — nunca viaja a Supabase.
// Best-effort: [] si no hay userId, localStorage falla (incógnito) o el dato
// guardado está corrupto.
export function diasActivosLocal(userId) {
  if (!userId) return [];
  try {
    const raw = window.localStorage.getItem(claveDiasActivosLocal(userId));
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.filter((f) => typeof f === 'string').sort();
  } catch {
    return [];
  }
}

// Registra HOY (o `fecha`) como día activo en el mapa local: dedup (no duplica
// si ya estaba), orden ascendente, capado a los últimos DIAS_LOCAL_MAX días
// (se descartan los más antiguos). Best-effort: no lanza si localStorage no
// está disponible.
export function registrarDiaActivoLocal(userId, fecha = fechaKey()) {
  if (!userId) return;
  try {
    const actuales = diasActivosLocal(userId);
    if (actuales.includes(fecha)) return; // ya registrado hoy: idempotente
    const siguientes = [...actuales, fecha].sort().slice(-DIAS_LOCAL_MAX);
    window.localStorage.setItem(claveDiasActivosLocal(userId), JSON.stringify(siguientes));
  } catch { /* best-effort */ }
}

// Registra actividad de HOY: recalcula y persiste la racha. Idempotente por día
// (varias actividades el mismo día no doblan). Devuelve el nuevo estado + banderas
// (para celebrar hito, escudo ganado, etc.). Best-effort: si falla la BD, devuelve
// el cálculo igual para que la UI no se quede sin feedback.
export async function registrarActividadDiaria(userId, hoy = fechaKey()) {
  const prev = await cargarRachaDiaria(userId);
  // Perdón por calendario: solo si hay hueco de días (gap >= 2) vale la pena
  // preguntar por la rotación — queries extra únicamente en el reencuentro.
  // Los overrides entran porque el perdón debe respetar el calendario REAL
  // ("pedí libre ese día" perdona; el patrón solo no lo sabría).
  // Best-effort: si falla, diasLibres 0 y la racha se comporta como siempre.
  let diasLibres = 0;
  const gapPrev = diasEntre(prev.fecha, hoy);
  if (userId && gapPrev != null && gapPrev >= 2) {
    try {
      const [rotRes, ovRes] = await Promise.all([
        supabase
          .from('rotaciones')
          .select('patron, fecha_inicio, tipo_institucion')
          .eq('user_id', userId)
          .eq('activa', true)
          .maybeSingle(),
        supabase
          .from('turno_overrides')
          .select('fecha, tipo')
          .eq('user_id', userId)
          .gt('fecha', prev.fecha)
          .lt('fecha', hoy),
      ]);
      diasLibres = diasLibresEntre(prev.fecha, hoy, rotRes.data || null, ovRes.data || []);
    } catch { /* best-effort */ }
  }
  const next = siguienteRachaDiaria(prev, hoy, diasLibres);
  registrarDiaActivoLocal(userId, hoy); // mismo punto donde ya marca el día: alimenta el mini-mapa visual
  publicarRachaWidget(next); // el widget nativo muestra la racha al día (no-op en web)
  if (next.yaHoy) return next; // ya contaba hoy: nada que persistir
  if (userId) {
    try {
      await supabase
        .from('profiles')
        .update({
          racha_diaria:         next.racha,
          racha_diaria_max:     next.max,
          racha_diaria_fecha:   next.fecha,
          racha_diaria_escudos: next.escudos,
          dias_activos_total:   next.dias,
        })
        .eq('user_id', userId);
    } catch { /* best-effort */ }
  }
  return next;
}

// Publica la racha al widget de pantalla de inicio (Android). `fechaActiva` es
// el último día que contó: el widget compara contra SU "hoy" al renderizar,
// así "✓ Hoy ya cuenta" nunca queda pegado al día anterior. Best-effort.
function publicarRachaWidget(estado) {
  if (!estado) return;
  publicarWidget({
    racha:       estado.racha || 0,
    escudos:     estado.escudos || 0,
    fechaActiva: estado.fecha || '',
  });
}
